import React, { useState, useRef, useEffect } from "react";
import { io } from "socket.io-client";
import { useLocation, useNavigate } from "react-router-dom";
import anshrlogo from "./ANS-Logo.svg";
import { MoveLeft } from "lucide-react";
import { useAuth } from "../../hooks/AuthContext";

// Remove /api/ if it's present in socket url
const SOCKET_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, "")
  : "";

const API_URL = process.env.REACT_APP_API_URL || "";

const socket = io(SOCKET_URL, { transports: ["websocket"] });

const ChatScreen = () => {
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [messagesLoaded, setMessagesLoaded] = useState(false);
  const messagesEndRef = useRef(null);

  const { state } = useLocation();
  const receivedGrievance = state?.data || state;
  const { user } = useAuth();
  const navigate = useNavigate();

  const getUserId = () => user?.employeeProfile?._id || null;

  // Helper to get auth headers with token for secured APIs
  const getAuthHeaders = () => {
    const token = localStorage.getItem("token"); // adjust if token stored elsewhere
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Group messages by date string
  const groupMessagesByDate = (msgs) => {
    const groups = {};
    msgs.forEach((msg) => {
      const msgDate = new Date(msg.date * 1000);
      const dateStr = msgDate.toDateString();
      if (!groups[dateStr]) groups[dateStr] = [];
      groups[dateStr].push(msg);
    });
    return groups;
  };

  // Get display label for a group date
  const getDateLabel = (dateStr) => {
    const msgDate = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    // Compare just the date part
    const isToday = msgDate.toDateString() === today.toDateString();
    const isYesterday = msgDate.toDateString() === yesterday.toDateString();
    if (isToday) return "Today";
    if (isYesterday) return "Yesterday";

    // Format as "2 November 2025"
    return `${msgDate.getDate()} ${msgDate.toLocaleString("default", {
      month: "long",
    })} ${msgDate.getFullYear()}`;
  };

  // Fetch all messages for ticketId on mount
  useEffect(() => {
    let isMounted = true;

    async function fetchMessages() {
      if (!receivedGrievance?.ticketId) return;
      try {
        const res = await fetch(
          `${API_URL}chat/${receivedGrievance.ticketId}/messages`,
          {
            headers: {
              ...getAuthHeaders(),
            },
          }
        );
        const data = await res.json();

        if (!isMounted) return;

        if (data && Array.isArray(data.messages) && data.messages.length > 0) {
          setMessages(data.messages);
        } else if (receivedGrievance.message) {
          const initialMsg = {
            ticketId: receivedGrievance.ticketId,
            senderId: receivedGrievance.senderId,
            senderType: "employee",
            text: receivedGrievance.message,
            type: "issue",
            date: Number(receivedGrievance.createdAt),
          };
          setMessages([initialMsg]);
          await fetch(`${API_URL}chat/${receivedGrievance.ticketId}/message`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...getAuthHeaders(),
            },
            body: JSON.stringify(initialMsg),
          });
        }
        setMessagesLoaded(true);
      } catch (err) {
        setMessagesLoaded(false);
        console.error("Error fetching messages:", err);
      }
    }
    fetchMessages();

    if (receivedGrievance?.ticketId) {
      socket.emit("joinTicket", receivedGrievance.ticketId);
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receivedGrievance?.ticketId]);

  // SOCKET: Listen for incoming new messages
  useEffect(() => {
    const handleNewMessage = (msg) => {
      if (
        messages.some(
          (m) =>
            m.text === msg.text &&
            m.senderId === msg.senderId &&
            m.date === msg.date
        )
      ) {
        return;
      }
      setMessages((prev) => [...prev, msg]);
    };
    socket.on("newMessage", handleNewMessage);
    return () => socket.off("newMessage", handleNewMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const getCurrentEpoch10Digit = () => {
    const date = new Date();
    const utc = date.getTime() + date.getTimezoneOffset() * 60000;
    const istTime = new Date(utc + 19800000);
    return Math.floor(istTime.getTime() / 1000);
  };

  // Handle sending
  const handleSendReply = () => {
    if (
      !replyText.trim() ||
      !user ||
      !user.employeeProfile ||
      !user.employeeProfile._id ||
      !receivedGrievance?.ticketId
    )
      return;

    const msgObj = {
      ticketId: receivedGrievance.ticketId,
      senderId: user.employeeProfile._id,
      senderType: user.role,
      text: replyText,
      type: "reply",
      date: getCurrentEpoch10Digit(),
    };
    socket.emit("sendMessage", msgObj);
    setReplyText("");
  };

  // Group sorted messages by date
  const groupedMessages = groupMessagesByDate(
    [...messages].sort((a, b) => a.date - b.date)
  );
  const dateGroups = Object.keys(groupedMessages).sort(
    (a, b) => new Date(a) - new Date(b)
  );
  // Call when chat screen opens to mark messages read
  useEffect(() => {
    if (!receivedGrievance?.ticketId || !user?.role) return;
    const updateStatus = async () => {
      try {
        await fetch(`${API_URL}chat/${receivedGrievance.ticketId}/status`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ userType: user.role }),
        });
      } catch (err) { }
    };
    updateStatus();
  }, [receivedGrievance?.ticketId, user?.role]);



  return (
    <div className="fixed inset-0 bg-gray-50 flex flex-col">
      <div className="flex-1 flex flex-col bg-white m-2 sm:m-4 rounded-xl shadow-lg overflow-hidden">
        {!receivedGrievance ? (
          <div className="flex-1 flex items-center justify-center text-gray-400 relative">
            <img
              src={anshrlogo}
              alt="AnsHR Logo"
              className="absolute inset-0 w-full h-full opacity-5 object-contain"
            />
            <img
              src={anshrlogo}
              alt="placeholder"
              className="relative z-10 w-48 sm:w-64 md:w-80"
            />
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center gap-3 sm:gap-4 border-b p-3 sm:p-4 bg-gray-50 flex-shrink-0">
              <MoveLeft
                className="cursor-pointer hover:text-[#2C4E86] transition flex-shrink-0"
                onClick={() => navigate(-1)}
                size={20}
              />
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-semibold text-sm sm:text-lg flex-shrink-0">
                {receivedGrievance.userName?.slice(0, 2).toUpperCase() || "?"}
              </div>
              <div className="flex flex-col min-w-0 flex-1">
                <span className="font-semibold text-sm sm:text-base text-gray-800 truncate capitalize">
                  {user?.name}
                </span>
                <span className="text-xs sm:text-sm text-gray-500 truncate uppercase">
                  {user?.role}
                </span>
              </div>
            </div>
            {/* Chat messages + date dividers */}
            <div className="flex-1 flex flex-col relative min-h-0">
              <img
                src={anshrlogo}
                alt="AnsHR Background"
                className="absolute inset-0 m-auto w-1/2 sm:w-1/3 h-auto opacity-5 object-contain pointer-events-none"
              />
              <div className="flex-1 overflow-y-auto p-3 sm:p-4 flex flex-col relative z-10 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400">
                {dateGroups.map((groupDate, groupIdx) => (
                  <React.Fragment key={groupDate}>
                    {/* Date divider */}
                    <div className="flex justify-center my-4">
                      <span className="px-4 py-1 rounded-full bg-gray-300 text-gray-800 text-xs font-medium shadow">
                        {getDateLabel(groupDate)}
                      </span>
                    </div>
                    {/* Messages for this date */}
                    {groupedMessages[groupDate].map((msg, idx) => {
                      const isOwn =
                        getUserId() &&
                        msg.senderId &&
                        msg.senderId.toString() === getUserId().toString();
                      return (
                        <div
                          key={`${msg.date}-${msg.senderId}-${msg.text}-${idx}`}
                          className={`flex gap-2 sm:gap-3 items-end ${isOwn ? "justify-end" : "justify-start"} my-2`} // << ADDED my-2
                        >
                          {/* Left bubble avatar */}
                          {!isOwn && (
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-semibold text-xs sm:text-sm flex-shrink-0">
                              {msg.senderType === "hr"
                                ? "ED"
                                : (receivedGrievance.userName
                                  ? receivedGrievance.userName.slice(0, 2).toUpperCase()
                                  : "?")}
                            </div>
                          )}
                          {/* Message bubble */}
                          <div
                            className={`px-3 sm:px-4 py-2 rounded-2xl max-w-[75%] sm:max-w-[60%] break-words shadow-md
              ${isOwn ? "bg-[#2C4E86] text-white rounded-br-none" : "bg-gray-200 text-gray-800 rounded-bl-none"}`}
                          >
                            <div className="text-sm sm:text-base">{msg.text}</div>
                            <div
                              className={`text-xs mt-1 text-right ${isOwn ? "text-gray-300" : "text-gray-500"}`}
                            >
                              {msg.date
                                ? new Date(msg.date * 1000).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                                : ""}
                            </div>
                          </div>
                          {/* Right bubble avatar */}
                          {isOwn && (
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-semibold text-xs sm:text-sm flex-shrink-0">
                              {user?.employeeProfile?.name
                                ? user.employeeProfile.name.slice(0, 2).toUpperCase()
                                : "?"}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}

                <div ref={messagesEndRef} />
              </div>
            </div>
            {/* Input */}
            <div className="flex gap-2 sm:gap-3 p-3 sm:p-4 border-t bg-gray-50 flex-shrink-0">
              <input
                type="text"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSendReply()}
                placeholder="Type a reply..."
                className="flex-1 p-2 sm:p-3 text-sm sm:text-base rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#2C4E86] focus:border-transparent"
                disabled={!messagesLoaded}
              />
              <button
                onClick={handleSendReply}
                disabled={!replyText.trim() || !messagesLoaded}
                className="px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base bg-[#2C4E86] text-white rounded-full hover:bg-[#1e3a5f] transition disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              >
                Send
              </button>
            </div>
          </>
        )}
      </div>
      <style jsx global>{`
        .scrollbar-thin::-webkit-scrollbar {
          width: 6px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: transparent;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
        .scrollbar-thin {
          scrollbar-width: thin;
          scrollbar-color: #d1d5db transparent;
        }
        body {
          overflow-x: hidden;
        }
      `}</style>
    </div>
  );
};

export default ChatScreen;
